/**
 * Copyright 2026 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { PropertyValues } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';

import { SpectrumElement } from '@adobe/spectrum-wc-core/element/index.js';
import { SizedMixin } from '@adobe/spectrum-wc-core/mixins/index.js';

import {
  ACTION_GROUP_ORIENTATIONS,
  ACTION_GROUP_STATIC_COLORS,
  ACTION_GROUP_VALID_SIZES,
  type ActionGroupOrientation,
  type ActionGroupSize,
} from './ActionGroup.types.js';

/**
 * An action group clusters related actions together with consistent
 * spacing, sizing, and orientation. It treats the whole strip as a single
 * stop in the keyboard sequence, with arrow keys moving between children.
 *
 * This base class owns the accessibility semantics, `accessible-label` →
 * `aria-label` management, `disabled` state contract, and child collection logic.
 * Rendering and styling live in the concrete SWC subclass.
 *
 * @slot - One or more `swc-action-button` or `swc-action-menu` elements.
 *
 * @attribute {ActionGroupSize} size - The size of the group and its children.
 */
export abstract class ActionGroupBase extends SizedMixin(SpectrumElement, {
  validSizes: ACTION_GROUP_VALID_SIZES,
  noDefaultSize: true,
}) {
  static override shadowRootOptions = {
    ...SpectrumElement.shadowRootOptions,
    delegatesFocus: true,
  };

  /**
   * The size of the action group. Propagated to all slotted children.
   */
  declare public size: ActionGroupSize;

  // ──────────────────
  //     SHARED API
  // ──────────────────

  /**
   * @internal
   *
   * Valid orientation values for validation.
   */
  static readonly ORIENTATIONS: readonly string[] = ACTION_GROUP_ORIENTATIONS;

  /**
   * @internal
   *
   * Valid static-color values for validation.
   */
  static readonly STATIC_COLORS: readonly string[] = ACTION_GROUP_STATIC_COLORS;

  /**
   * The layout direction of the action group.
   *
   * When set to `"vertical"`, the group stacks children vertically and
   * `FocusgroupNavigationController` moves focus with the Up/Down arrow
   * keys instead of Left/Right.
   *
   * Note: this property does NOT set `aria-orientation` on the host. The
   * host's role is fixed to `role="group"`, and `aria-orientation` is only
   * a supported ARIA attribute on roles that expose it (`toolbar`,
   * `listbox`, `menu`, etc.), not `group`, regardless of this component's
   * roving-tabindex keyboard model. Setting it anyway fails axe's
   * `aria-allowed-attr` rule.
   *
   * @default horizontal
   */
  @property({ type: String, reflect: true })
  public orientation: ActionGroupOrientation = 'horizontal';

  /**
   * Accessible label for the group. Reflected to `aria-label` on the host.
   *
   * Providing a label is recommended whenever the strip has a distinct
   * purpose (e.g., "Image adjustments" or "Edit"). An empty value removes
   * the `aria-label` attribute.
   */
  @property({ type: String, attribute: 'accessible-label' })
  public accessibleLabel = '';

  /**
   * Whether the group and all of its children are disabled.
   *
   * Sets `aria-disabled="true"` on the host and propagates `aria-disabled`
   * to each managed child. Children remain keyboard-reachable so that screen
   * reader users can still discover the group. Native `disabled` is not
   * applied to children.
   */
  @property({ type: Boolean, reflect: true })
  public disabled = false;

  // ──────────────────────
  //     IMPLEMENTATION
  // ──────────────────────

  /**
   * @internal
   *
   * The managed children: all `swc-action-button` and `swc-action-menu`
   * elements assigned to the default slot. Populated by the concrete
   * subclass via `@queryAssignedElements`.
   */
  @queryAssignedElements({ flatten: true })
  protected managedChildren!: HTMLElement[];

  /**
   * Focuses the first enabled child in the group.
   *
   * The SWC concrete class overrides this with `FocusgroupNavigationController`,
   * restoring focus to the last active item when memory is enabled. This
   * implementation serves as a fallback for non-SWC subclasses.
   */
  public override focus(options?: FocusOptions): void {
    const firstEnabled = this.managedChildren?.find(
      (el) =>
        !el.hasAttribute('disabled') &&
        el.getAttribute('aria-disabled') !== 'true'
    );
    firstEnabled?.focus(options);
  }

  /**
   * @internal
   *
   * Handles slot-change events. Propagates `aria-disabled` to newly slotted
   * children when the group is disabled. Concrete subclasses call
   * `super.handleSlotchange()` and then propagate visual attributes.
   */
  protected handleSlotchange(): void {
    if (this.disabled) {
      this.propagateDisabledToChildren();
    }
  }

  protected override firstUpdated(changed: PropertyValues<this>): void {
    super.firstUpdated(changed);
    this.setAttribute('role', 'group');
  }

  protected override updated(changed: PropertyValues<this>): void {
    super.updated(changed);

    if (changed.has('accessibleLabel')) {
      if (this.accessibleLabel) {
        this.setAttribute('aria-label', this.accessibleLabel);
      } else {
        this.removeAttribute('aria-label');
      }
    }

    if (changed.has('disabled')) {
      if (this.disabled) {
        this.setAttribute('aria-disabled', 'true');
      } else {
        this.removeAttribute('aria-disabled');
      }
      this.propagateDisabledToChildren();
    }

    if (window.__swc?.DEBUG) {
      const constructor = this.constructor as typeof ActionGroupBase;
      if (!constructor.ORIENTATIONS.includes(this.orientation)) {
        window.__swc.warn(
          this,
          `<${this.localName}> element expects the "orientation" attribute to be one of the following: ${constructor.ORIENTATIONS.join(', ')}.`,
          'https://opensource.adobe.com/spectrum-web-components/components/action-group/',
          { issues: [...constructor.ORIENTATIONS] }
        );
      }
    }
  }

  private propagateDisabledToChildren(): void {
    // `aria-disabled` is set on the child host element. For AT to announce the
    // child as disabled, each child component must forward the host attribute to
    // its inner interactive element. `swc-action-button` already does this
    // (observes `aria-disabled` and forwards to its inner <button>).
    // `swc-action-menu` will need the same treatment when it is built — it must
    // forward `aria-disabled` from its host to its inner trigger element and
    // apply disabled appearance via :host([aria-disabled="true"]) CSS.
    for (const child of this.managedChildren ?? []) {
      if (this.disabled) {
        child.setAttribute('aria-disabled', 'true');
      } else {
        child.removeAttribute('aria-disabled');
      }
    }
  }
}
